import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import type { SchemaElement } from "../../../api/restaurantSchema";

// Точките по местата: най-много толкова (в два реда), после само надписът.
const MAX_SEAT_DOTS = 12;
const DOTS_PER_SINGLE_ROW = 6;

/** Редовете точки за `seats` места: до 6 на един ред, до 12 в два. */
const seatDotRows = (seats: number) => {
  const shown = Math.min(Math.max(seats, 0), MAX_SEAT_DOTS);
  if (shown <= DOTS_PER_SINGLE_ROW) return [shown];
  const firstRow = Math.ceil(shown / 2);
  return [firstRow, shown - firstRow];
};

/**
 * Размерът на номера по дължина: къс номер е едър, дълъг („Тераса 5",
 * „E2E-RACE") се смалява и пренася на втори ред, вместо да се реже с „…".
 */
const nameSizeClass = (name: string, isCompact: boolean) => {
  if (name.length <= 4) return isCompact ? "text-[20px]" : "text-[25px]";
  if (name.length <= 8) return isCompact ? "text-[16px]" : "text-[19px]";
  return isCompact ? "text-[14px]" : "text-[16px]";
};

const TILE_BASE = "relative flex flex-col rounded-[12px] text-left transition-[background,border-color,box-shadow] duration-150";

/**
 * Масата като плочка: голям номер, чекбокс за множествен избор горе вдясно,
 * точка на всяко място, „4 места". Клик върху плочката избира само нея;
 * чекбоксът добавя/маха от избора. Без hover зависимости — работи с пръст.
 */
export const TableCard = ({
  table,
  isSelected,
  isCompact,
  onSelect,
  onToggle,
}: {
  table: SchemaElement;
  isSelected: boolean;
  isCompact: boolean;
  onSelect: () => void;
  onToggle: () => void;
}) => {
  const { t } = useTranslation();
  const seats = table.seats ?? 0;
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={`${TILE_BASE} cursor-pointer select-none border outline-none focus-visible:shadow-[0_0_0_3px_rgba(217,115,13,0.25)] ${
        isCompact ? "min-h-[92px] gap-2 p-2.5" : "min-h-[104px] gap-2.5 p-3.5"
      } ${
        isSelected
          ? "border-brand bg-brand-tint shadow-[inset_0_0_0_1px_var(--color-brand)]"
          : "border-shade-2 bg-surface hover:border-shade-4"
      }`}
    >
      {/* Над два реда (до 50 знака) се реже — пълното име е в title. */}
      <div
        title={table.name ?? undefined}
        className={`line-clamp-2 break-words pr-7 font-bold leading-[1.15] tracking-[-0.6px] tabular-nums ${
          isSelected ? "text-brand-deep-ink" : "text-ink"
        } ${nameSizeClass(table.name ?? "", isCompact)}`}
      >
        {table.name}
      </div>
      <button
        type="button"
        role="checkbox"
        aria-checked={isSelected}
        aria-label={t("tablesConfig.grid.selectTable", { name: table.name ?? "" })}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        // Мишената е 36px — чекбоксът вътре е 18px.
        className="absolute right-1 top-1 flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent p-0"
      >
        <span
          className={`flex h-[18px] w-[18px] items-center justify-center rounded-[5px] text-[10px] transition-colors duration-150 ${
            isSelected ? "border border-brand bg-brand text-white" : "border-[1.5px] border-shade-4 bg-surface text-transparent"
          }`}
        >
          <CheckOutlined />
        </span>
      </button>
      <div className="mt-auto flex flex-col gap-2">
        <div className="flex flex-col gap-[3px]" aria-hidden>
          {seatDotRows(seats).map((count, rowIndex) => (
            <div key={rowIndex} className="flex gap-[3px]">
              {Array.from({ length: count }, (_, dotIndex) => (
                <span
                  key={dotIndex}
                  className={`h-[6px] w-[6px] rounded-full ${isSelected ? "bg-brand" : "bg-brand/45"}`}
                />
              ))}
            </div>
          ))}
        </div>
        <div className={`text-[12.5px] font-medium leading-[1.2] tabular-nums ${isSelected ? "text-brand-deep-ink" : "text-body"}`}>
          {t("restaurantSchema.counts.seats", { count: seats })}
        </div>
      </div>
    </div>
  );
};

/** Пунктирната плочка „+ Нова маса" в края на мрежата. */
export const AddTableCard = ({ isCompact, onClick }: { isCompact: boolean; onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${TILE_BASE} cursor-pointer items-center justify-center gap-1.5 border border-dashed border-shade-4 bg-transparent text-body hover:border-brand hover:bg-brand-tint hover:text-brand-deep-ink ${
        isCompact ? "min-h-[92px] p-2.5" : "min-h-[104px] p-3.5"
      }`}
    >
      <PlusOutlined className="text-[16px]" />
      <span className="text-[13px] font-semibold leading-[1.2]">{t("tablesConfig.grid.newTable")}</span>
    </button>
  );
};
